import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { updateSensorRequest } from "../../store/actions/sensors.actions";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { sensorType } from "../../config/sensorTypeEnum";
import MapDrawMarkerComponent from "shared-components/src/components/map/mapDrawMarker.component";

const UpdateSensorsComponent = ({
  sensor,
  open,
  onClose,
  validationSchema,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const waterSystems = useSelector((state) => state?.waterSystems?.data);

  const initialValues = {
    waterSystem: sensor?.system?.id || "",
    type: sensor?.attributes?.type || "",
    eui: sensor?.attributes?.eui || "",
    label: sensor?.attributes?.label || "",
    id_thing: sensor?.attributes?.id_thing || "",
    latitude: sensor?.attributes?.position[0] || "",
    longitude: sensor?.attributes?.position[1] || "",
  };

  const createSensorPayload = (sensorData) => {
    const payload = {
      id: sensorData.id,
      eui: sensorData.eui || null,
      position: sensorData.position,
      type: sensorData.type,
      label: sensorData.label,
      id_thing: sensorData.id_thing,
      water_system: sensorData.water_system,
    };

    return payload;
  };

  const handleSave = (values) => {
    const updatedSensor = {
      ...sensor,
      type: values.type,
      eui: values.eui,
      position: [values.latitude, values.longitude],
      label: values.label,
      water_system: values.waterSystem,
      id_thing: values.id_thing,
    };
    dispatch(updateSensorRequest(createSensorPayload(updatedSensor)));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        "& .MuiDialog-paper": {
          minWidth: "70%",
        },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {(formikProps) => (
          <Form>
            <DialogTitle id="alert-dialog-title">
              {t("setup-sensors-page.update-form-title")}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                <Grid
                  container
                  rowSpacing={2}
                  columnSpacing={2}
                  style={{ marginTop: "20px" }}
                >
                  <Grid item xs={12} sm={12} md={6}>
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={12} md={12}>
                        <FormControl fullWidth>
                          <InputLabel id="water-system-select-label">
                            {`${t(
                              "setup-sensors-page.add.source-user-label"
                            )}*`}
                          </InputLabel>
                          <Select
                            label="Type of sensor"
                            id={`waterSystem`}
                            name={`waterSystem`}
                            labelId="water-system-select-label"
                            value={formikProps.values.waterSystem}
                            onChange={(e) =>
                              formikProps.setFieldValue(
                                "waterSystem",
                                e.target.value
                              )
                            }
                            error={formikProps.errors.waterSystem}
                          >
                            <MenuItem value="">
                              <em>{t("none")}</em>
                            </MenuItem>
                            {Object.values(waterSystems).map(
                              (waterSystem, i) => (
                                <MenuItem key={i} value={waterSystem.id}>
                                  {t(waterSystem.attributes.label)}
                                </MenuItem>
                              )
                            )}
                          </Select>
                          <ErrorMessage
                            name="waterSystem"
                            component="div"
                            style={{
                              color: "#fd7777",
                              fontSize: "0.75rem",
                              marginTop: "3px",
                              marginLeft: "14px",
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <FormControl fullWidth>
                          <InputLabel id="type-select-label">
                            {t("setup-sensors-page.add.type-sensor-label")}
                          </InputLabel>
                          <Select
                            label={`${t(
                              "setup-sensors-page.add.type-sensor-label"
                            )}*`}
                            id={`type`}
                            name={`type`}
                            labelId="type-select-label"
                            value={formikProps.values.type}
                            onChange={(e) =>
                              formikProps.setFieldValue("type", e.target.value)
                            }
                            error={formikProps.errors.type}
                          >
                            <MenuItem value="">
                              <em>{t("none")}</em>
                            </MenuItem>
                            {Object.values(sensorType).map((option, i) => (
                              <MenuItem key={i} value={option}>
                                {t(option)}
                              </MenuItem>
                            ))}
                          </Select>
                          <ErrorMessage
                            name="type"
                            component="div"
                            style={{
                              color: "#fd7777",
                              fontSize: "0.75rem",
                              marginTop: "3px",
                              marginLeft: "14px",
                            }}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <Field
                          as={TextField}
                          label={`${t("setup-sensors-page.add.name")}*`}
                          name={`label`}
                          variant="outlined"
                          fullWidth
                          error={formikProps.errors.label}
                          helperText={formikProps.errors.label}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <Field
                          as={TextField}
                          label={`${t("setup-sensors-page.add.id_thing")}*`}
                          name={`id_thing`}
                          variant="outlined"
                          fullWidth
                          error={formikProps.errors.id_thing}
                          helperText={formikProps.errors.id_thing}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <Field
                          as={TextField}
                          label={t("setup-sensors-page.add.device-eui")}
                          name={`eui`}
                          variant="outlined"
                          fullWidth
                          error={formikProps.errors.eui}
                          helperText={formikProps.errors.eui}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <Field
                          as={TextField}
                          label={`${t("setup-sensors-page.add.latitude")}*`}
                          name={`latitude`}
                          variant="outlined"
                          fullWidth
                          error={formikProps.errors.latitude}
                          helperText={formikProps.errors.latitude}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={12}>
                        <Field
                          as={TextField}
                          label={`${t("setup-sensors-page.add.longitude")}*`}
                          name={`longitude`}
                          variant="outlined"
                          fullWidth
                          error={formikProps.errors.longitude}
                          helperText={formikProps.errors.longitude}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}>
                    <MapDrawMarkerComponent
                      markerPosition={{
                        lat: formikProps?.values?.latitude,
                        lng: formikProps?.values?.longitude,
                      }}
                      onMarkerPositionChange={(value) => {
                        formikProps.setFieldValue("latitude", value?.lat || "");
                        formikProps.setFieldValue(
                          "longitude",
                          value?.lng || ""
                        );
                      }}
                      polygons={sensor.system?.attributes?.polygons}
                    ></MapDrawMarkerComponent>
                  </Grid>
                </Grid>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                {t("setup-sensors-page.add.cancel")}
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {t("setup-sensors-page.btn-update")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default UpdateSensorsComponent;
